import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import { getConferenceTimezone, AGENDA_START_HOUR, AGENDA_END_HOUR, CURRENT_EVENT_YEAR } from '../schedule.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// Build the AGENDA_START_HOUR..AGENDA_END_HOUR window for `date` (YYYY-MM-DD)
// in `tz`. Built via dayjs .tz() so DST transitions are handled correctly.
// Lives in shared/ (not server/) because the calendar needs it to place gap
// blocks without a round trip. Pure — no Vue APIs.
export function dayWindow(date, tz = getConferenceTimezone(CURRENT_EVENT_YEAR)) {
    const dayStart = dayjs.tz(date, 'YYYY-MM-DD', tz).hour(AGENDA_START_HOUR).minute(0).second(0).millisecond(0);
    const dayEnd = dayjs.tz(date, 'YYYY-MM-DD', tz).hour(AGENDA_END_HOUR).minute(0).second(0).millisecond(0);
    return { dayStart: dayStart.toDate(), dayEnd: dayEnd.toDate() };
}

// The whole-hour agenda time-filter window for a day, derived from that day's
// real programming bounds (earliest start floored, latest end ceiled) instead of
// the fixed AGENDA_START_HOUR..AGENDA_END_HOUR. `bounds` is the { start, end } ISO
// pair from catalogDayBounds — the FULL possible set for the day (all catalog
// sessions + the viewer's custom events, before content filters), so the slider
// and calendar grid span every session/event and the user can see the gaps they
// might fill (UNB-609). Both the client slider and the server time_range filter
// call this so their percent→hour math stays in lockstep. Falls back to the
// fixed window when bounds are unknown (day='all', an empty day). Pure — no Vue.
export function agendaWindowHours(bounds, tz = getConferenceTimezone(CURRENT_EVENT_YEAR)) {
    if (!bounds?.start || !bounds?.end) {
        return { startHour: AGENDA_START_HOUR, endHour: AGENDA_END_HOUR };
    }
    const s = dayjs(bounds.start).tz(tz);
    const e = dayjs(bounds.end).tz(tz);
    let startHour = Math.floor(s.hour() + s.minute() / 60);
    let endHour = Math.ceil(e.hour() + e.minute() / 60);
    if (!Number.isFinite(startHour) || startHour < 0) startHour = AGENDA_START_HOUR;
    if (!Number.isFinite(endHour) || endHour > 24) endHour = 24;
    if (endHour <= startHour) endHour = Math.min(startHour + 1, 24);
    return { startHour, endHour };
}
